import { FC, useEffect, useState } from "react";
import {
    Box,
    Button,
    FormControl,
    MenuItem,
    Modal,
    Select,
    Typography,
} from "@mui/material";
import { useSingleUserControl } from "../../../context/SuperAdmin/SingleUserControlContext";
import { getRolesByOrgId, postUsersInRole } from "../../../api/broker";
import { toast } from "react-toastify";
import { iDataRoles } from "../../../interfaces/SuperAdmin/Pages/iDataRoles";

interface Props {
    isOpen: boolean;
    setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
    organizationId: string;
    organizationName: string;
}

const AddRoleToOrgModal: FC<Props> = ({
    isOpen,
    setIsOpen,
    organizationId,
    organizationName,
}) => {
    const { rdsUser, refreshUserData } = useSingleUserControl();
    const [roles, setRoles] = useState<iDataRoles[]>([]);
    const [selectedRole, setSelectedRole] = useState<string>("");
    const [isLoading, setIsLoading] = useState(false);
    const [isAdding, setIsAdding] = useState(false);

    const handleClose = () => {
        if (!isAdding) {
            setIsOpen(false);
            setSelectedRole("");
        }
    };

    // Fetch roles for this organization
    useEffect(() => {
        const fetchRoles = async () => {
            if (isOpen && organizationId) {
                setIsLoading(true);
                try {
                    const res = await getRolesByOrgId(organizationId);
                    if (res.data.statusCode === 200) {
                        setRoles(res.data.body);
                    } else {
                        toast.error("Error al cargar roles");
                    }
                } catch (error) {
                    console.error(error);
                    toast.error("Error al cargar roles");
                } finally {
                    setIsLoading(false);
                }
            }
        };

        fetchRoles();
    }, [isOpen, organizationId]);

    const handleAdd = async () => {
        if (!rdsUser || !selectedRole) return;

        setIsAdding(true);
        try {
            const res = await postUsersInRole({
                id_rol: selectedRole,
                id_usuarios: [rdsUser.id_usuario],
            });

            if (res.data.statusCode === 201) {
                toast.success("Rol agregado exitosamente");
                await refreshUserData();
                handleClose();
            } else {
                toast.error("Error al agregar rol");
            }
        } catch (error) {
            console.error(error);
            toast.error("Error al agregar rol");
        } finally {
            setIsAdding(false);
        }
    };

    return (
        <Modal
            open={isOpen}
            onClose={handleClose}
            sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "rgba(0, 0, 0, 0.3)",
            }}
        >
            <Box
                sx={{
                    width: 400,
                    bgcolor: "background.paper",
                    borderRadius: 2,
                    p: 4,
                    boxShadow: 24,
                    minWidth: "500px",
                    display: "flex",
                    flexDirection: "column",
                    gap: 3,
                }}
            >
                <Typography variant="h6" component="h2">
                    Agregar Rol a {organizationName}
                </Typography>

                {isLoading ? (
                    <Typography>Cargando roles...</Typography>
                ) : (
                    <>
                        <Box>
                            <Typography variant="body1" sx={{ mb: 1, fontWeight: 500 }}>
                                Seleccionar Rol
                            </Typography>
                            <FormControl fullWidth>
                                <Select
                                    value={selectedRole}
                                    onChange={(e) => setSelectedRole(e.target.value)}
                                    disabled={isAdding}
                                    displayEmpty
                                >
                                    <MenuItem value="" disabled>
                                        Seleccione un rol
                                    </MenuItem>
                                    {roles.map((role) => (
                                        <MenuItem key={role.id_rol} value={role.id_rol}>
                                            {role.nombre}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>

                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "flex-end",
                                gap: 2,
                                mt: 2,
                            }}
                        >
                            <Button
                                variant="outlined"
                                onClick={handleClose}
                                disabled={isAdding}
                                sx={{
                                    borderRadius: 14,
                                    border: "1px solid #336B87",
                                    color: "#336B87",
                                    "&:hover": {
                                        backgroundColor: "#336B87",
                                        color: "#EEF3F6",
                                    },
                                }}
                            >
                                Cancelar
                            </Button>
                            <Button
                                variant="contained"
                                sx={{ background: "#336B87", borderRadius: 14 }}
                                disabled={isAdding || !selectedRole}
                                onClick={handleAdd}
                            >
                                {isAdding ? "Agregando..." : "Agregar"}
                            </Button>
                        </Box>
                    </>
                )}
            </Box>
        </Modal>
    );
};

export default AddRoleToOrgModal;
